import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ShoppingBag, ShoppingCart, Home as HomeIcon, Heart, User as UserIcon, Scale, X as XIcon, MessageCircle, Sparkles } from "lucide-react";
import { useWishlist } from "@/hooks/useWishlist";
import { useCompare } from "@/hooks/useCompare";
import { BrandMark } from "@/components/shared/BrandMark";
import { CartDrawer, useCart } from "@/pages/ShopCart";
import { SemanticSearchBar } from "@/components/shop/SemanticSearchBar";
import { CurrencySelector } from "@/components/shared/CurrencySelector";
import { useDirectChatNotifier } from "@/hooks/useDirectChatNotifier";

// ------------------------ Shared shell ------------------------
export const ShopShell: React.FC<{ children: React.ReactNode; title?: string; description?: string; canonical?: string; hideFooter?: boolean; showBottomNav?: boolean; showCart?: boolean; noindex?: boolean }> = ({
  children, title, description, canonical, hideFooter, showBottomNav, showCart = true, noindex = false,
}) => {
  const [cartOpen, setCartOpen] = useState(false);
  const cart = useCart();
  const itemCount = (cart.data?.items || []).reduce((s: number, i: any) => s + i.quantity, 0);
  const { unread } = useDirectChatNotifier();
  const location = useLocation();
  const onAiCart = location.pathname.startsWith("/ai-cart");
  const onShop   = location.pathname.startsWith("/shop") && !onAiCart;
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100" data-testid="shop-shell">
      {(title || noindex) && (
        <Helmet>
          {title && <title>{title}</title>}
          {description && <meta name="description" content={description} />}
          {canonical && <link rel="canonical" href={canonical} />}
          {description && <meta property="og:description" content={description} />}
          {title && <meta property="og:title" content={title} />}
          {/* Robots directive — noindex on private surfaces (cart, checkout,
              order confirmation, search-with-query) prevents personal /
              transactional URLs from polluting Google's index. */}
          {noindex && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
      )}
      <header className="sticky top-0 z-30 bg-white/85 dark:bg-slate-950/85 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4">
          <Link to="/" data-testid="shop-brand" className="flex items-center gap-2 font-display font-bold text-lg tracking-tight shrink-0" title="SociBaba — home">
            <BrandMark
              showIcon
              quiet
              variant="shop"
              suffix="Shop"
              iconClassName="h-9 w-9 mr-2 inline-block align-middle"
              className="font-display font-bold text-lg tracking-tight flex items-center"
            />
          </Link>
          {/* Header search — desktop only. Mobile shoppers already have the
              bottom-nav Search icon which pipes into /shop/search directly,
              so cramming an input into a 400px row would push the cart +
              currency selector off-screen.
              We use a `<div>` here (not <form>) because SemanticSearchBar
              renders its OWN <form>. Nesting would trigger a React hydration
              warning. */}
          <div className="hidden sm:block flex-1 relative max-w-lg mx-auto min-w-0">
            <SemanticSearchBar />
          </div>
          {/* Mobile-only flex spacer so brand and right cluster split evenly. */}
          <div className="flex-1 sm:hidden" />
          {/* Currency + cart show on every viewport — global shoppers on
              mobile need the same currency switcher desktop users get. */}
          <div className="flex items-center gap-1.5 sm:gap-3 text-sm shrink-0">
            <Link to="/shop" data-testid="shop-nav-all" className="hidden sm:inline-block hover:text-[color:var(--sb-saffron)]">All</Link>
            <CurrencySelector />
            <Link
              to="/messages"
              data-testid="shop-nav-messages"
              className="relative p-2 hover:text-[color:var(--sb-saffron)]"
              aria-label="Open messages"
            >
              <MessageCircle className="h-5 w-5" />
              {unread.total > 0 && (
                <span data-testid="shop-nav-messages-badge" className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-fuchsia-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unread.total > 9 ? "9+" : unread.total}
                </span>
              )}
            </Link>
            {showCart && (
              <button
                onClick={() => setCartOpen(true)}
                data-testid="cart-open-btn"
                className="relative p-2 hover:text-[color:var(--sb-saffron)]"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[color:var(--sb-saffron)] text-white text-[10px] font-bold flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
            )}
            <Link to="/" className="hidden md:inline-block hover:text-[color:var(--sb-saffron)]">Dashboard →</Link>
          </div>
        </div>
      </header>
      {/* Shop ⇄ AI Cart™ mode switcher — always visible on both surfaces
          so a shopper can flip between "browse verified sellers" and
          "paste a link, get the honest score" in one tap. */}
      <div
        className="sticky top-[57px] z-20 bg-white/85 dark:bg-slate-950/85 backdrop-blur-md border-b border-slate-200 dark:border-slate-800"
        data-testid="shop-mode-switcher"
      >
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-3 sm:px-6 py-2 flex items-center justify-center gap-1.5">
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-1">
            <Link
              to="/shop"
              data-testid="mode-switcher-shop"
              className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                onShop
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
              aria-current={onShop ? "page" : undefined}
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              <span>Shop</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-widest opacity-70 ml-1">
                Verified sellers
              </span>
            </Link>
            <Link
              to="/ai-cart"
              data-testid="mode-switcher-ai-cart"
              className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                onAiCart
                  ? "bg-gradient-to-r from-fuchsia-500 to-orange-500 text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
              aria-current={onAiCart ? "page" : undefined}
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>AI Cart™ Search</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-widest opacity-70 ml-1">
                Paste any link
              </span>
            </Link>
          </div>
        </div>
      </div>
      <main className={`max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-4 sm:px-6 py-8 ${showBottomNav ? "pb-24 md:pb-8" : ""}`}>{children}</main>
      {showCart && <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />}
      <CompareTrayFab />
      {!hideFooter && (
      <footer className="mt-16 border-t border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-950/60 backdrop-blur">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-4 sm:px-6 py-8 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-4 justify-between items-center">
          <div>© {new Date().getFullYear()} SociBaba · Every product carries a <strong className="text-[color:var(--sb-saffron)]">SIBA Score</strong>. Trust made visible.</div>
          <nav className="flex flex-wrap gap-4">
            <Link to="/about">About</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
        </div>
      </footer>
      )}
      {showBottomNav && <MobileBottomNav />}
    </div>
  );
};

// ------------------------ Mobile Bottom Nav ------------------------
const MobileBottomNav: React.FC = () => {
  const { count } = useWishlist();
  const items = [
    { to: "/", label: "Home", icon: HomeIcon, testId: "bottom-nav-home", badge: 0 },
    { to: "/shop/search?q=", label: "Search", icon: Search, testId: "bottom-nav-search", badge: 0 },
    { to: "/shop", label: "Shop", icon: ShoppingBag, testId: "bottom-nav-shop", badge: 0 },
    { to: "/wishlist", label: "Wishlist", icon: Heart, testId: "bottom-nav-wishlist", badge: count },
    { to: "/dashboard", label: "Profile", icon: UserIcon, testId: "bottom-nav-profile", badge: 0 },
  ];
  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-950/95 border-t border-slate-200 dark:border-slate-800 backdrop-blur"
      data-testid="shop-bottom-nav"
    >
      <div className="grid grid-cols-5 h-16">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.label}
              to={it.to}
              data-testid={it.testId}
              className="relative flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 hover:text-[color:var(--sb-saffron)] transition-colors"
            >
              <div className="relative">
                <Icon className="h-5 w-5" strokeWidth={2} />
                {it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 text-[9px] font-bold bg-orange-500 text-white flex items-center justify-center rounded-full">
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                )}
              </div>
              <span>{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// ------------------------ Compare Tray FAB ------------------------
// Floating bar that materialises when >=2 items are in the compare tray.
// One tap opens the /compare page pre-populated via the ?ids= URL.
const CompareTrayFab: React.FC = () => {
  const { ids, count, clear } = useCompare();
  const nav = useNavigate();
  if (count === 0) return null;
  const idsQuery = ids.map(encodeURIComponent).join("~");
  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 dark:bg-white text-white dark:text-slate-900 border border-slate-800 dark:border-slate-200 shadow-2xl flex items-center gap-3 px-4 py-2.5 max-w-[calc(100vw-24px)]"
      data-testid="shop-compare-tray"
    >
      <Scale className="h-4 w-4" strokeWidth={2} />
      <div className="text-sm font-display font-semibold">
        {count} in compare
        <span className="ml-1 text-[10px] uppercase tracking-wider opacity-60">tray</span>
      </div>
      <button
        type="button"
        onClick={() => nav(`/compare?ids=${idsQuery}`)}
        disabled={count < 2}
        data-testid="shop-compare-tray-open"
        className="ml-2 inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 transition-colors"
      >
        Compare
      </button>
      <button
        type="button"
        onClick={clear}
        aria-label="Clear compare tray"
        data-testid="shop-compare-tray-clear"
        className="h-6 w-6 flex items-center justify-center text-white/60 dark:text-slate-500 hover:text-white dark:hover:text-slate-900"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
